import type { CountryCode } from "@config/site/types";
import { getInvalidPostalCodesForCountry } from "@components/commerce/baseForms/utils/getInvalidPostalCodesForCountry";
import type {
    PostalCodeValidationResult,
    PostalCodeValidator,
    PostalCodeValidatorParams,
} from "./types";
import { validateGB } from "./validators/validateGB";
import { validateSE } from "./validators/validateSE";
import { validateIE } from "./validators/validateIE";
import { getQueryClient } from "@lib/helpers/queryClient";
import { errorResult, successResult } from "./utils";
import { validateIN } from "./validators/validateIN";

export const getPostalCodeValidators = (country: CountryCode): PostalCodeValidator[] | null => {
    // Return market specific validator or the base validators if none exists.
    // Market specific validators should implement the base validators themselves if needed.
    return validators[country] ?? null;
};

export const invalidPostalCodesValidator: PostalCodeValidator = async (args) => {
    const { postalCode, isBillingForm, headers, country } = args;

    // Don't run validation for billing form
    if (isBillingForm) {
        return successResult();
    }
    const queryClient = getQueryClient();
    // Trim whitespace from the input value
    const normalizedValue = postalCode?.toLocaleLowerCase()?.trim() ?? "";
    if (!normalizedValue) return successResult();

    const invalidPostalCodes = (await getInvalidPostalCodesForCountry(country, queryClient)) ?? [];
    if (invalidPostalCodes.includes(normalizedValue)) {
        return errorResult(headers?.errors?.undeliverableAddress ?? "", "undeliverableAddress");
    }
    return successResult();
};

const validators: Partial<Record<CountryCode, PostalCodeValidator[]>> = {
    // GB deliberately does not use the default invalidPostalCodesValidator because it uses a custom implementation that checks for patterns
    GB: [validateGB],
    SE: [validateSE],
    ES: [invalidPostalCodesValidator],
    IE: [validateIE],
    IN: [validateIN],
};

/**
 *
 * @param args PostalCodeValidatorParams
 * @returns PostalCodeValidationResult
 */
export const getPostalCodeValidationResult = async (
    args: PostalCodeValidatorParams
): Promise<PostalCodeValidationResult | undefined> => {
    const country = args.country;

    const validators = getPostalCodeValidators(country);
    if (!validators) {
        return Promise.resolve(successResult());
    }
    if (Array.isArray(validators) && validators.length === 0) {
        return Promise.resolve(successResult());
    }

    const results = await Promise.all(validators.map((validator) => validator(args)));

    // If any of the validators returns a value, return that value as the error.
    const errorResult = results.find((result) => result.isValid === false);

    if (errorResult) {
        return errorResult;
    }
    return results.find((result) => result.isValid === true);
};
